import React, { useState } from 'react';
import {
    Button,
    Checkbox,
    FormControl,
    FormLabel,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Radio,
    RadioGroup,
    Stack,
    Text,
    Textarea,
    useToast,
    VStack
} from '@chakra-ui/react';
import { SPORT_OPTIONS, sportLabel } from '../../config/marketControlCatalog';
import { tradingClosureService } from '../../services/tradingClosureService';

const SPORTS = SPORT_OPTIONS.filter(o => o.id != null).map(o => o.id as number);

export type TradingClosureAction = 'close' | 'recover';

type Scope = 'ALL' | 'SPORTS';

interface TradingClosureDialogProps {
    action: TradingClosureAction;
    isOpen: boolean;
    onClose: () => void;
    onDone: () => void;
}

export const TradingClosureDialog: React.FC<TradingClosureDialogProps> = ({ action, isOpen, onClose, onDone }) => {
    const [scope, setScope] = useState<Scope>('ALL');
    const [sportIds, setSportIds] = useState<number[]>([]);
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const toast = useToast();

    const closing = action === 'close';
    const verb = closing ? 'Close trading' : 'Recover trading';
    const target = scope === 'ALL' ? 'all sports' : sportIds.map(sportLabel).join(', ');
    const canSubmit = scope === 'ALL' || sportIds.length > 0;

    const toggleSport = (sportId: number) =>
        setSportIds(ids => ids.includes(sportId) ? ids.filter(id => id !== sportId) : [...ids, sportId]);

    const reset = () => {
        setScope('ALL');
        setSportIds([]);
        setReason('');
    };

    const submit = async () => {
        const selected = scope === 'ALL' ? null : sportIds;
        setSubmitting(true);
        try {
            if (closing) {
                await tradingClosureService.close(selected, reason.trim() || undefined);
            } else {
                await tradingClosureService.recover(selected);
            }
            toast({ title: `${verb}: ${target}`, status: 'success', duration: 3000, isClosable: true });
            reset();
            onDone();
            onClose();
        } catch (error) {
            toast({ title: `Failed to ${verb.toLowerCase()}`, status: 'error', duration: 3000, isClosable: true });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={submitting ? () => {} : onClose} isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{verb}</ModalHeader>
                <ModalCloseButton isDisabled={submitting} />
                <ModalBody>
                    <VStack align="stretch" spacing={4}>
                        <FormControl>
                            <FormLabel>Scope</FormLabel>
                            <RadioGroup value={scope} onChange={value => setScope(value as Scope)}>
                                <Stack>
                                    <Radio value="ALL">All sports</Radio>
                                    <Radio value="SPORTS">Selected sports</Radio>
                                </Stack>
                            </RadioGroup>
                            {scope === 'SPORTS' && (
                                <Stack pl={6} pt={2}>
                                    {SPORTS.map(sportId => (
                                        <Checkbox
                                            key={sportId}
                                            isChecked={sportIds.includes(sportId)}
                                            onChange={() => toggleSport(sportId)}
                                        >
                                            {sportLabel(sportId)}
                                        </Checkbox>
                                    ))}
                                </Stack>
                            )}
                        </FormControl>
                        {closing && (
                            <FormControl>
                                <FormLabel>Reason</FormLabel>
                                <Textarea
                                    value={reason}
                                    onChange={e => setReason(e.target.value)}
                                    maxLength={255}
                                    placeholder="Optional"
                                />
                            </FormControl>
                        )}
                        {canSubmit && (
                            <Text color={closing ? 'red.600' : 'blue.600'}>
                                {closing
                                    ? `Every open market of ${target} will be suspended.`
                                    : scope === 'ALL'
                                        ? 'Open markets of all sports will be offered again. Closed competitions stay closed.'
                                        : `Open markets of ${target} will be offered again, unless all sports are closed.`}
                            </Text>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter gap={3}>
                    <Button variant="ghost" onClick={onClose} isDisabled={submitting}>Cancel</Button>
                    <Button colorScheme={closing ? 'red' : 'blue'} onClick={submit} isLoading={submitting}
                            isDisabled={!canSubmit}>
                        {verb}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
